import * as yup from 'yup'
import { array, object, string } from 'yup'
import { normalizeHourlyFrequency } from '../utils'

export const vitalSchema = object({
  // priority: string(),
  captured_date: string().required('Date is required'),

  time: string().required('Time is required'),

  unit: string().required('Unit is required'),

  value: string().required('Value is required'),
  vital_type: string().required('Vital type is required')
})

export const admissionSchema = object({
  care_provider: string().required('Care provider is required'),
  note: string(),
  telemetry: yup.boolean().optional() as unknown as yup.BooleanSchema<boolean>,
  unit: string().required('Unit is required')
  // patient: string()
})

export const orderSchema = object({
  name: string().required('Test type is required'),
  notes: string().optional()
})

export const familySchema = object({
  additional_info: string(),
  // full_name: string().required('Name is required'),
  medical_conditions: array(),
  relationship: string().required('Relationship is required')
})

export const surgerySchema = object({
  location: string().required('Location is required'),
  surgery_date: string().required('Date is required'),
  surgery_type: string().required('Surgery name is required')
})

export const socialSchema = object({
  date: string().required('Date is required'),
  // social_name: string().required('Social name is required'),
  social_type: string().required('Social type is required'),
  status: string().required('Status is required')
})

export const medicalSchema = object({
  comments: string(),
  icd_codings_attributes: array()
    .of(
      object().shape({
        icd_code_id: string().required('ICD coding is required')
      })
    )
    // .required('Diagnosis/ICD coding is required')
    .min(1, 'Diagnosis/ICD coding is required'),
  status: string().required('Status is required')
})

export const frequencySchema = yup
  .object({
    // --- COMPLEX VALIDATION FOR THE INTERVAL FIELD ---
    interval: yup.string().when('type', {
      is: 'hourly',
      otherwise: schema => schema.notRequired(),
      then: schema =>
        schema
          .required('Interval is required for hourly frequency')
          .test(
            'interval-format',
            'Interval must be in format "Every X hours" or "QXH" (e.g., "Every 5 hours", "Q4H")',
            value => {
              // If there's a value, test its format.
              if (value) {
                const normalized = normalizeHourlyFrequency(value)
                return normalized !== null
              }
              // If there's no value, the 'required' rule will catch it.
              // Returning true here prevents a double error message.
              return true
            }
          ) // Not required and not validated for 'daily'.
    }),

    // Fields are defined simply. The complex logic is on the 'interval' field.
    periods: yup.array().of(yup.string().oneOf(['morning', 'afternoon', 'night'])),

    specific_times: yup.array().of(yup.string()),

    type: yup.string().oneOf(['hourly', 'daily']).required('Frequency type is required')
  })
  // --- SIMPLIFIED OBJECT-LEVEL TEST FOR OVERALL COMPLETENESS ---
  .test(
    'frequency-completeness',
    'Please complete one of the frequency configurations: hourly with an interval, or daily with either periods or specific times.',
    function (value) {
      if (!value) return false

      const { type, interval, periods, specific_times } = value

      // Check for a valid HOURLY configuration
      if (type === 'hourly') {
        // It's valid if the interval exists and is not just whitespace.
        // The schema's .when() clause already validated the format.
        return !!(interval && interval.trim() !== '')
      }

      // Check for a valid DAILY configuration
      if (type === 'daily') {
        const hasPeriods = periods && periods?.length > 0
        const hasSpecificTimes =
          specific_times && specific_times?.length > 0 && specific_times.every(time => time && time.trim() !== '')

        // It's valid if it has periods OR specific times.
        return hasPeriods || hasSpecificTimes
      }

      return false
    }
  )

export const medicationSchema = object({
  administer_at_hospital: yup.boolean(),
  allergy_end_date: yup.string().nullable(),
  allergy_start_date: yup.string().when('icd_codings_attributes', {
    is: (icd_codings_attributes: any[]) => icd_codings_attributes && icd_codings_attributes?.length > 0,
    otherwise: schema => schema.notRequired(),
    then: schema => schema.required('Allergy start date is required when ICD codes are provided')
  }),

  comment: string().optional(),
  strength: string().optional(),

  custom_frequency: object().nullable().optional(),

  dose_quantity: string().optional(),
  // .when('is_doctor', {
  //   is: true,
  //   then: schema => schema.required('Dosage is required'),
  //   otherwise: schema => schema.optional()
  // }),
  // dosage: string().when('strength', {
  //   is: (strength: string[]) => !strength,
  //   then: schema => schema.required('Dosage is required'),
  //   otherwise: schema => schema.optional()
  // }),
  dose_unit: string().optional(),
  // .when('is_doctor', {
  //   is: true,
  //   then: schema => schema.required('Dose unit is required'),
  //   otherwise: schema => schema.optional()
  // }),
  // start_time: string().required('Start time is required'),
  // end_date: string(),
  end_at: yup.mixed<string | Date>().nullable().optional(),

  form: string().required('Dosage form is required'),

  icd_codings_attributes: yup
    .array()
    .of(
      object().shape({
        icd_code_id: yup.string()
      })
    )
    .optional(),

  // id: string(),
  medication_type: string().required('Medication type is required'),

  nf_drug_name: string().required('Drug name is required'),

  once: yup.boolean().nullable().optional() as unknown as yup.BooleanSchema<boolean>,

  reaction: yup.string().when('icd_codings_attributes', {
    is: (icd_codings_attributes: any[]) => icd_codings_attributes && icd_codings_attributes?.length > 0,
    otherwise: schema => schema.notRequired(),
    then: schema => schema.required('Reaction is required when ICD codes are provided')
  }),

  route: string().required('Route of administration is required'),
  // quantity: string().optional(),
  start_at: yup.mixed<string | Date>().required('Start date is required'),
  status: string().optional(),
  title: string().optional()
})
